<template>
    <div v-show="visible" class="color-picker-fullscreen-overlay" @click.self="close">
        <div class="color-picker-fullscreen-modal">
            <div class="color-picker-fullscreen-header">
                <h4 class="color-picker-fullscreen-title">{{ title }}</h4>
                <a href="javascript:;" class="color-picker-fullscreen-close" @click="close">
                    <img src="/maincss/images/tool_close.svg" alt="Close" />
                </a>
            </div>
            <div class="color-picker-fullscreen-search">
                <div class="color-picker-fullscreen-search-inner">
                    <span class="search-icon" aria-hidden="true">
                        <!-- simple magnifying glass icon -->
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M7.66927 14.5007C3.9026 14.5007 0.835938 11.434 0.835938 7.66732C0.835938 3.90065 3.9026 0.833984 7.66927 0.833984C11.4359 0.833984 14.5026 3.90065 14.5026 7.66732C14.5026 11.434 11.4359 14.5007 7.66927 14.5007ZM7.66927 1.83398C4.44927 1.83398 1.83594 4.45398 1.83594 7.66732C1.83594 10.8807 4.44927 13.5007 7.66927 13.5007C10.8893 13.5007 13.5026 10.8807 13.5026 7.66732C13.5026 4.45398 10.8893 1.83398 7.66927 1.83398Z" fill="#929292"/>
                            <path d="M14.6676 15.1676C14.5409 15.1676 14.4143 15.1209 14.3143 15.0209L12.9809 13.6876C12.7876 13.4943 12.7876 13.1743 12.9809 12.9809C13.1743 12.7876 13.4943 12.7876 13.6876 12.9809L15.0209 14.3143C15.2143 14.5076 15.2143 14.8276 15.0209 15.0209C14.9209 15.1209 14.7943 15.1676 14.6676 15.1676Z" fill="#929292"/>
                            </svg>
                    </span>
                    <input
                        v-model="searchQuery"
                        type="text"
                        class="form-control"
                        :placeholder="searchPlaceholder"
                    />
                </div>
            </div>
            <div class="color-picker-fullscreen-grid-wrap">
                <div class="color-picker-fullscreen-grid">
                    <div
                        v-for="(color, index) in filteredColors"
                        :key="index"
                        class="color-picker-fullscreen-cell"
                        @click="onSelectColor(color)"
                    >
                        <div
                            :class="['color-picker-fullscreen-swatch', { selected: isColorSelected(color, index) }]"
                            :title="color.color_name || ''"
                        >
                            <span v-if="color.color_name" class="swatch-hover-name">{{ color.color_name }}</span>
                            <img
                                v-if="color.swatch_file"
                                :src="color.swatch_file"
                                :alt="color.color_name"
                                class="swatch-img"
                            />
                            <div
                                v-else
                                class="swatch-solid"
                                :style="{ background: color.color_value || '#ccc' }"
                            ></div>
                            <div v-if="isColorSelected(color, index)" class="swatch-check">
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M11.6666 3.5L5.24992 9.91667L2.33325 7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>
                        <div class="swatch-hex">{{ color.color_value || '—' }}</div>
                        <div class="swatch-pms">{{ color.color_pms || '—' }}</div>
                    </div>
                </div>
            </div>
            <div class="color-picker-fullscreen-footer">
                <span class="color-picker-fullscreen-count">{{ filteredColors.length }} colors</span>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "ColorPickerFullScreen",
    props: {
        visible: { type: Boolean, default: false },
        title: { type: String, default: "Colors" },
        colors: { type: Array, default: () => [] },
        selectedColor: { type: [String, undefined], default: undefined },
        selectedColorPms: { type: [String, undefined], default: undefined },
        selectedColorId: { type: [Number, String, undefined], default: undefined },
        colorListValue: { type: Array, default: () => [] },
        searchPlaceholder: { type: String, default: "Search colors..." }
    },
    data() {
        return {
            searchQuery: ""
        };
    },
    computed: {
        filteredColors() {
            if (!this.colors || !this.colors.length) return [];
            const q = (this.searchQuery || "").trim().toLowerCase();
            if (!q) return this.colors;
            return this.colors.filter((c) => {
                const name = (c.color_name || "").toLowerCase();
                const value = (c.color_value || "").toLowerCase();
                const pms = (c.color_pms || "").toString().toLowerCase();
                return name.includes(q) || value.includes(q) || pms.includes(q);
            });
        },
        /** Keys for list colors: one check per hex, prefer selected. */
        firstMatchKeysForColorList() {
            const set = new Set();
            if (!this.colorListValue || !this.colorListValue.length || !this.filteredColors.length) return set;
            const colors = this.filteredColors;
            for (const listHex of this.colorListValue) {
                const h = this.toHex(listHex);
                if (this.selectedColorId != null) {
                    const selectedIdx = colors.findIndex((c) => c.color_id === this.selectedColorId);
                    if (selectedIdx >= 0 && this.toHex(colors[selectedIdx].color_value) === h) {
                        const c = colors[selectedIdx];
                        set.add(c.color_id != null ? c.color_id : "idx_" + selectedIdx);
                        continue;
                    }
                }
                let idx = -1;
                if (this.selectedColorPms != null && String(this.selectedColorPms).trim() !== "") {
                    idx = colors.findIndex((c) => this.swatchMatchesHexAndPms(c, h));
                }
                if (idx < 0) idx = colors.findIndex((c) => this.toHex(c.color_value) === h);
                if (idx >= 0) {
                    const c = colors[idx];
                    set.add(c.color_id != null ? c.color_id : "idx_" + idx);
                }
            }
            return set;
        }
    },
    watch: {
        visible(val) {
            if (val) {
                this.searchQuery = "";
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = '';
            }
        }
    },
    beforeUnmount() {
        document.body.style.overflow = '';
    },
    methods: {
        /** Match hex + PMS so one swatch per variant. */
        swatchMatchesHexAndPms(color, hex) {
            if (!color || this.toHex(color.color_value) !== hex) return false;
            if (this.selectedColorPms == null || this.selectedColorPms === "") return true;
            const pms = (color.color_pms || "").toString().trim();
            const selPms = (this.selectedColorPms || "").toString().trim();
            return pms !== "" && selPms !== "" && pms === selPms;
        },
        toHex(val) {
            if (!val || typeof val !== "string") return (val || "").toString().trim().toLowerCase();
            const rgb = /rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/.exec(val);
            if (rgb) {
                const r = parseInt(rgb[1], 10);
                const g = parseInt(rgb[2], 10);
                const b = parseInt(rgb[3], 10);
                return "#" + [r, g, b].map((x) => ("0" + Math.max(0, Math.min(255, x)).toString(16)).slice(-2)).join("");
            }
            return val.trim().toLowerCase();
        },
        isColorSelected(color, index) {
            if (!color) return false;
            
            const colorHex = this.toHex(color.color_value);
            if (this.colorListValue && this.colorListValue.length > 0) {
                const key = color.color_id != null ? color.color_id : (index != null ? "idx_" + index : null);
                if (key != null && this.firstMatchKeysForColorList.has(key)) {
                    return true;
                }
                if (this.firstMatchKeysForColorList.size > 0) {
                    const hexMatchesAny = [...this.colorListValue].some(h => this.toHex(h) === colorHex);
                    if (hexMatchesAny) return false;
                }
            }
            
            if (this.selectedColorId != null && color.color_id != null) {
                return this.selectedColorId === color.color_id;
            }
            
            const sel = this.toHex(this.selectedColor);
            if (colorHex !== sel && (!color.swatch_file || this.selectedColor !== color.swatch_file)) {
                return false;
            }
            
            if (this.selectedColorPms != null && color.color_pms != null) {
                if (this.selectedColorPms !== color.color_pms) return false;
                if (this.selectedColorId != null && color.color_id != null && this.selectedColorId !== color.color_id) {
                    return false;
                }
            }
            
            return true;
        },
        onSelectColor(color) {
            this.$emit("select", color);
            this.$emit("close");
        },
        close() {
            this.$emit("close");
        }
    }
};
</script>

<style scoped>
.color-picker-fullscreen-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
}
.color-picker-fullscreen-modal {
    background: #fff;
    border-radius: 8px;
    max-width: 895px;
    width: 100%;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}
.color-picker-fullscreen-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 24px;
    border-bottom: 1px solid #eee;
}
.color-picker-fullscreen-title {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: #121212 !important;
    font-size: 20px;
    line-height: 20px;
}
.color-picker-fullscreen-close img {
    width: 14px;
    height: 14px;
}
.color-picker-fullscreen-search {
    padding: 24px 24px 0px 24px;
}
.color-picker-fullscreen-search-inner {
    position: relative;
}
.color-picker-fullscreen-search-inner .search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #9e9e9e;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
}
.color-picker-fullscreen-search-inner input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    border: 1px solid #BEBEBE66 !important;
    padding: 12px 12px 15px 34px !important;
}
.color-picker-fullscreen-search-inner input::placeholder {
    font-size: 14px;
    line-height: 14px;
    color: #000000;
    font-weight: 400;
}
.color-picker-fullscreen-grid-wrap {
    flex: 1;
    padding: 32px 24px;
    min-height: 200px;
    max-height: 70vh;
    overflow-y: auto;
}

/* Custom scrollbar styling for color grid */
.color-picker-fullscreen-grid-wrap::-webkit-scrollbar {
    width: 8px;
}

.color-picker-fullscreen-grid-wrap::-webkit-scrollbar-track {
    background: transparent;
}

.color-picker-fullscreen-grid-wrap::-webkit-scrollbar-thumb {
    background-color: #929292;
    border-radius: 4px;
}

.color-picker-fullscreen-grid-wrap::-webkit-scrollbar-thumb:hover {
    background-color: #929292;
}

/* Firefox scrollbar */
.color-picker-fullscreen-grid-wrap {
    scrollbar-width: thin;
    scrollbar-color: #929292 transparent;
}
.color-picker-fullscreen-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
    gap: 24px;
}
.color-picker-fullscreen-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    cursor: pointer;
    margin-bottom: 16px;
}
.color-picker-fullscreen-swatch {
    position: relative;
    width: 100%;
    aspect-ratio: 1;
    max-width: 66px;
    border: 1px solid #999;
    border-radius: 8px;
    cursor: pointer;
    overflow: visible;
}
.color-picker-fullscreen-swatch.selected {
    border-color: #000;
    border-width: 2px;
}

.swatch-hover-name {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    bottom: 100%;
    margin-bottom: 6px;
    padding: 4px 8px;
    background: #333;
    color: #fff;
    font-size: 11px;
    white-space: nowrap;
    border-radius: 4px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease;
    z-index: 2;
}
.swatch-hover-name::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: #333;
}
.color-picker-fullscreen-swatch:hover .swatch-hover-name {
    opacity: 1;
}
.color-picker-fullscreen-swatch .swatch-img,
.color-picker-fullscreen-swatch .swatch-solid {
    width: 64px;
    height: 64px;
    display: block;
    border-radius: 8px;
}
.color-picker-fullscreen-swatch.selected .swatch-img,
.color-picker-fullscreen-swatch.selected .swatch-solid {
    width: 63px;
    height: 63px;
}
.swatch-img {
    object-fit: cover;
}
.swatch-check {
    position: absolute;
    top: 2px;
    right: 2px;
    width: 20px;
    height: 20px;
    background: #000;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}
.swatch-check svg {
    width: 14px;
    height: 14px;
}
.swatch-hex {
    font-size: 14px;
    color: #000000;
    font-weight: 600;
    text-align: left;
    line-height: 14px;
    margin-top: 12px;
}
.swatch-pms {
    font-size: 14px;
    line-height: 14px;
    color: #606060;
    text-align: left;
}
.color-picker-fullscreen-footer {
    padding: 10px 20px;
    border-top: 1px solid #eee;
    font-size: 0.85rem;
    color: #666;
}
</style>
